import React from "react";
import { Link } from "gatsby";
import styled from "@emotion/styled";

import { DarkModeToggle } from "~/components/DarkModeToggle";
import { OpenInNew } from "~/components/Icons";

const Wrapper = styled.header({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "0.75rem 1.5rem",
  paddingBottom: "1.5rem",
  marginBottom: "3.5rem",
  borderBottom: "1px solid var(--border)",
});

const Logo = styled(Link)({
  fontSize: "1.4rem",
  fontWeight: 500,
  color: "var(--textTitle)",
  textDecoration: "none",
});

const Nav = styled.nav({
  display: "flex",
  alignItems: "center",
  gap: "1.5rem",
  "& a": {
    color: "var(--textNormal)",
    textDecoration: "none",
  },
  "& a:hover": {
    textDecoration: "underline",
  },
  "& a[aria-current='page']": {
    fontWeight: 600,
  },
  "@media (max-width: 640px)": {
    width: "100%",
  },
});

const ToggleWrapper = styled.div({
  marginLeft: "auto",
  // Matches the previous Material UI switch's height and left spacing
  padding: "8px 0 8px 24px",
});

const ResumeLink = styled.a({
  display: "inline-flex",
  alignItems: "center",
  gap: "0.25em",
  whiteSpace: "nowrap",
  "& svg": {
    fontSize: "1em",
  },
});

type HeaderProps = {
  title: string;
  pathname: string;
};

const Header = ({ title, pathname }: HeaderProps) => {
  const currentPath = pathname.replace(/\/+$/, "") || "/";
  const ariaCurrent = (path: string) =>
    currentPath === path ? ("page" as const) : undefined;

  return (
    <Wrapper>
      <Logo to="/">{title}</Logo>
      <Nav>
        <Link to="/" aria-current={ariaCurrent("/")}>
          Home
        </Link>
        <Link to="/contact" aria-current={ariaCurrent("/contact")}>
          Contact
        </Link>
        <ResumeLink
          href="/resume.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          Resume <OpenInNew />
          <span className="visually-hidden"> (PDF, opens in new tab)</span>
        </ResumeLink>
        <ToggleWrapper>
          <DarkModeToggle />
        </ToggleWrapper>
      </Nav>
    </Wrapper>
  );
};

export default Header;
